import { BKJIcon, BKJSelectSearch } from '@bookjane2/bookjane-design-library';
import { BKJSideModal } from 'components/BKJSideModal';
import { Flex } from 'components/Flex';
import { P } from 'components/Typography';
import { useTranslation } from 'hooks/useTranslation';
import {
  AllocationAppliedCount,
  AllocationLocationRow,
  AllocationModalTitle,
  EmptyStateMessage,
} from 'pages/Settings/pages/Organization/SkillsPage/SkillsPage.styled';
import { OrganizationSkill } from 'pages/Settings/pages/Organization/SkillsPage/store/organizationSkills.store.types';
import { useAuthRouteDataContext } from 'providers/AuthRouteDataProvider/AuthRouteDataProvider.context';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';

export const SkillLocationsAllocationModal: FC<{
  skill: OrganizationSkill | null;
  onClose: () => void;
}> = ({ skill, onClose }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const {
    locationFilter: { data: locations },
  } = useAuthRouteDataContext();

  useEffect(() => setSearchTerm(''), [skill]);

  const appliedLocationIds = useMemo(
    () => new Set(skill?.communitiesSkills.map(({ community }) => `${community.displayId}`) ?? []),
    [skill],
  );

  const visibleLocations = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    return locations
      .filter((location) => location.label.toLowerCase().includes(normalizedSearchTerm))
      .sort(
        (a, b) =>
          Number(appliedLocationIds.has(b.id)) - Number(appliedLocationIds.has(a.id)) ||
          a.label.localeCompare(b.label),
      );
  }, [locations, searchTerm, appliedLocationIds]);

  return (
    <BKJSideModal
      isOpen={!!skill}
      onClose={onClose}
      headerComponent={t('location_settings:LOCATIONS_ALLOCATION')}
    >
      {skill && (
        <Flex flexDirection="column" width="100%">
          <Flex flexDirection="column" gap="20px" padding="40px 32px 24px 32px">
            <AllocationModalTitle>
              {t('settings:SKILL')}: {skill.name}
            </AllocationModalTitle>
            <P>{t('settings:SKILL_ALLOCATION_TEXT')}</P>
            <BKJSelectSearch
              name="searchLocations"
              value={searchTerm}
              placeholder={t('settings:SEARCH_LOCATIONS')}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value)}
            />
          </Flex>
          <AllocationAppliedCount>
            {t('settings:LOCATIONS_APPLIED', { count: appliedLocationIds.size })}
          </AllocationAppliedCount>
          {visibleLocations.map((location) => (
            <AllocationLocationRow key={location.id}>
              {appliedLocationIds.has(location.id) ? <BKJIcon iconName="Checkmark" /> : <span />}
              {location.label}
            </AllocationLocationRow>
          ))}
          {!visibleLocations.length && (
            <EmptyStateMessage>{t('common:NO_RESULTS')}</EmptyStateMessage>
          )}
        </Flex>
      )}
    </BKJSideModal>
  );
};
